import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  IconButton,
  Chip,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@material-ui/core';
import { Close } from '@material-ui/icons';

const FilterableDataGridModal = ({ 
  open, 
  onClose, 
  title, 
  columns, 
  rows, 
  totalItems,
  onRowDoubleClick,
  onPageChange: externalOnPageChange,
  isModal = true,
  searchText: externalSearchText,
  setSearchText: externalSetSearchText,
}) => {
  const [localSearchText, setLocalSearchText] = useState(externalSearchText || '');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [selectedRow, setSelectedRow] = useState(null);
  
  const searchInputRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setLocalSearchText(externalSearchText || '');
  }, [externalSearchText]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [open]);

  const enhancedColumns = useMemo(() => {
    return columns.map(col => {
      const baseColumn = {
        filterable: true,
        filterType: 'text',
        flex: 1,
        ...col,
        headerName: col.headerName || col.field,
      };

      if (col.filterType === 'number') {
        return {
          ...baseColumn,
          align: 'right',
        };
      }

      return baseColumn;
    });
  }, [columns]);

  const filteredRows = useMemo(() => {
    if (!externalSearchText?.trim()) {
      return rows;
    }

    const searchTerm = externalSearchText.toLowerCase().trim();
    return rows.filter(row => {
      return enhancedColumns.some(col => {
        if (col.filterable === false) return false;
        const value = row[col.field];
        return value && value.toString().toLowerCase().includes(searchTerm);
      });
    });
  }, [rows, externalSearchText, enhancedColumns]);

  const pageCount = Math.ceil((externalOnPageChange ? totalItems : filteredRows.length) / pageSize);

  const handleRowDoubleClick = useCallback((row) => {
    if (onRowDoubleClick) {
      onRowDoubleClick(row);
    }
  }, [onRowDoubleClick]);

  const handleRowClick = useCallback((row) => {
    setSelectedRow(row);
  }, []);

  const handleClearSearch = useCallback(() => {
    setLocalSearchText('');
    if (externalSetSearchText) {
      externalSetSearchText('');
    }
    setPage(0);
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [externalSetSearchText]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    
    if (externalOnPageChange) {
      externalOnPageChange({
        page: newPage,
        pageSize: pageSize,
        searchText: externalSearchText
      });
    }
  }, [externalOnPageChange, pageSize, externalSearchText]);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
    
    if (externalOnPageChange) {
      externalOnPageChange({
        page: 0,
        pageSize: newPageSize,
        searchText: externalSearchText
      });
    }
  }, [externalOnPageChange, externalSearchText]);

  const displayRows = useMemo(() => {
    const sourceRows = externalOnPageChange ? rows : filteredRows;
    if (externalOnPageChange) {
      return sourceRows;
    }
    return sourceRows.slice(page * pageSize, (page + 1) * pageSize);
  }, [externalOnPageChange, rows, filteredRows, page, pageSize]);

  const totalItemsCount = externalOnPageChange ? totalItems : filteredRows.length;

  const handleSearchChange = (value) => {
    setLocalSearchText(value);
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      console.log('###')
      if (externalSetSearchText) {
        externalSetSearchText(value);
      }
      setPage(0);
    }, 700);
  };


  const CustomPagination = () => {
    const startItem = page * pageSize + 1;
    const endItem = Math.min((page + 1) * pageSize, totalItemsCount);

    return (
      <Box display="flex" justifyContent="space-between" alignItems="center" p={1} bgcolor="#f5f5f5" borderRadius={1}>
        <Box>
          <Chip 
            label={`${startItem}-${endItem} de ${totalItemsCount}`} 
            size="small" 
            variant="outlined"
          />
        </Box>
        
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            size="small"
            disabled={page === 0}
            onClick={() => handlePageChange(0)}
            variant="outlined"
          >
            Primera
          </Button>
          
          <Button
            size="small"
            disabled={page === 0}
            onClick={() => handlePageChange(page - 1)}
            variant="outlined"
          >
            Anterior
          </Button>

          <Box mx={1}>
            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
              Página {page + 1} de {pageCount}
            </span>
          </Box>

          <Button
            size="small"
            disabled={page >= pageCount - 1}
            onClick={() => handlePageChange(page + 1)}
            variant="outlined"
          >
            Siguiente
          </Button>

          <Button
            size="small"
            disabled={page >= pageCount - 1}
            onClick={() => handlePageChange(pageCount - 1)}
            variant="outlined"
          >
            Última
          </Button>
        </Box>

        <Box>
          <TextField
            select
            size="small"
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            variant="outlined"
            style={{ minWidth: '120px' }}
          >
            <option value={25}>25 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </TextField>
        </Box>
      </Box>
    );
  };

  const renderCellValue = (column, value) => {
    if (column.renderCell) {
      const rendered = column.renderCell({ value });
      return React.isValidElement(rendered) ? rendered : String(rendered);
    }
    
    if (column.filterType === 'number' && value != null && value !== '') {
      const numericValue = Number(value);
      if (!isNaN(numericValue)) {
        return numericValue.toFixed(2);
      }
    }
    
    return value != null ? String(value) : '';
  };

  if (!isModal) {
    return (
      <Box style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <TableContainer 
          component={Paper} 
          style={{ 
            height: '100%', 
            overflow: 'auto',
            boxShadow: 'none',
            border: '1px solid #e0e0e0'
          }}
        >
          <Table stickyHeader size="small" style={{ minWidth: '100%' }}>
            <TableHead>
              <TableRow>
                {enhancedColumns.map((column) => (
                  <TableCell
                    key={column.field}
                    align={column.align || 'left'}
                    style={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      padding: '6px 8px',
                      fontSize: '0.8rem',
                      lineHeight: '1.2',
                      borderBottom: '2px solid #e0e0e0'
                    }}
                  >
                    {column.headerName}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.length > 0 ? (
                displayRows.map((row, index) => (
                  <TableRow
                    key={row.id || index}
                    onClick={() => handleRowClick(row)}
                    onDoubleClick={() => {
                      console.log('##### double click')
                      handleRowDoubleClick(row)
                    }}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: selectedRow?.id === row.id ? '#e3f2fd' : 'transparent',
                      height: '32px',
                    }}
                    hover
                  >
                    {enhancedColumns.map((column) => (
                      <TableCell
                        key={column.field}
                        align={column.align || 'left'}
                        style={{
                          padding: '4px 8px',
                          fontSize: '0.8rem',
                          lineHeight: '1.2',
                          borderBottom: '1px solid #f0f0f0'
                        }}
                      >
                        {renderCellValue(column, row[column.field])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell 
                    colSpan={enhancedColumns.length} 
                    align="center"
                    style={{
                      padding: '16px',
                      fontSize: '0.9rem',
                      color: '#666'
                    }}
                  >
                    No se encontraron registros
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      style={{ height: '95vh' }}
    >
      <DialogTitle style={{ padding: '16px 24px' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <strong style={{ fontSize: '1.2rem', marginRight: '12px' }}>{title}</strong>
            <Chip 
              label={`${totalItemsCount} registros encontrados`} 
              size="small" 
              color="primary"
              variant="outlined"
            />
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent style={{ 
        height: '70vh', 
        padding: '16px', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2}>
          {/* Input de búsqueda con Bootstrap styling */}
          <div style={{ flex: 1, position: 'relative' }}>
            <input
              ref={searchInputRef}
              type="text"
              value={localSearchText}
              onChange={(e) => {
                handleSearchChange(e.target.value);
              }}
              placeholder="Escribe para buscar..."
              style={{
                width: '100%',
                height: '38px',
                padding: '6px 12px',
                fontSize: '14px',
                lineHeight: '1.42857143',
                color: '#333',
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: 'inset 0 1px 1px rgba(0,0,0,.075)',
                transition: 'border-color ease-in-out .15s, box-shadow ease-in-out .15s',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#66afe9';
                e.target.style.outline = '0';
                e.target.style.boxShadow = 'inset 0 1px 1px rgba(0,0,0,.075), 0 0 8px rgba(102,175,233,.6)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#ccc';
                e.target.style.boxShadow = 'inset 0 1px 1px rgba(0,0,0,.075)';
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                  }
                  if (externalSetSearchText) {
                    externalSetSearchText(localSearchText);
                  }
                  setPage(0);
                }
              }}
            />
          </div>
          
          {/* Botón Limpiar con Bootstrap styling */}
          <button
            type="button"
            onClick={handleClearSearch}
            disabled={!externalSearchText && !localSearchText}
            style={{
              height: '38px',
              padding: '6px 12px',
              fontSize: '14px',
              fontWeight: '400',
              lineHeight: '1.42857143',
              color: (!externalSearchText && !localSearchText) ? '#777' : '#333',
              backgroundColor: (!externalSearchText && !localSearchText) ? '#eee' : '#fff',
              border: '1px solid #ccc',
              borderRadius: '4px',
              cursor: (!externalSearchText && !localSearchText) ? 'not-allowed' : 'pointer',
              minWidth: '80px',
              boxSizing: 'border-box'
            }}
            onMouseEnter={(e) => {
              if (externalSearchText || localSearchText) {
                e.target.style.backgroundColor = '#e6e6e6';
                e.target.style.borderColor = '#adadad';
              }
            }}
            onMouseLeave={(e) => {
              if (externalSearchText || localSearchText) {
                e.target.style.backgroundColor = '#fff';
                e.target.style.borderColor = '#ccc';
              }
            }}
          >
            Limpiar
          </button>
        </Box>
        
        <Box style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <TableContainer 
            component={Paper} 
            style={{ 
              height: '100%', 
              overflow: 'auto',
              boxShadow: 'none',
              border: '1px solid #e0e0e0'
            }}
          >
            <Table stickyHeader size="small" style={{ minWidth: '100%' }}>
              <TableHead>
                <TableRow>
                  {enhancedColumns.map((column) => (
                    <TableCell
                      key={column.field}
                      align={column.align || 'left'}
                      style={{
                        fontWeight: 'bold',
                        backgroundColor: '#f5f5f5',
                        padding: '6px 8px',
                        fontSize: '0.8rem',
                        lineHeight: '1.2',
                        borderBottom: '2px solid #e0e0e0'
                      }}
                    >
                      {column.headerName}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayRows.length > 0 ? (
                  displayRows.map((row, index) => (
                    <TableRow
                      key={row.id || index}
                      onClick={() => handleRowClick(row)}
                      onDoubleClick={() => {
                        console.log('##### double click')
                        handleRowDoubleClick(row)
                      }}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: selectedRow?.id === row.id ? '#e3f2fd' : 'transparent',
                        height: '32px',
                      }}
                      hover
                    >
                      {enhancedColumns.map((column) => (
                        <TableCell
                          key={column.field}
                          align={column.align || 'left'}
                          style={{
                            padding: '4px 8px',
                            fontSize: '0.8rem',
                            lineHeight: '1.2',
                            borderBottom: '1px solid #f0f0f0'
                          }}
                        >
                          {renderCellValue(column, row[column.field])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell 
                      colSpan={enhancedColumns.length} 
                      align="center"
                      style={{
                        padding: '16px',
                        fontSize: '0.9rem',
                        color: '#666'
                      }}
                    >
                      No se encontraron registros
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {totalItemsCount > 0 && (
          <Box mt={1}>
            <CustomPagination />
          </Box>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px' }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          color="primary"
          style={{ minWidth: '120px' }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterableDataGridModal;